import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { BrandHeader } from './BrandHeader';
import { AgentsIslandBadge, useAgentsPillReady } from './AgentsIslandBadge';
import { ChipSlotProvider } from './chip-morph/ChipSlotContext';
import { MilestoneCard } from './MilestoneCard';
import { CosmicBackdrop } from './CosmicBackdrop';
import { Sidebar } from './sidebar';
import { SearchDialog } from './sidebar/components/SearchDialog';
import { NavId } from './NavRail';
import { FloatingChat } from './FloatingChat';
import { useChatDockWidth } from '../../lib/use-chat-dock-width';
import { TaskSidePanel } from './task-side-panel/TaskSidePanel';
import { PhoneVerifyModal } from './PhoneVerifyModal';
import { useConversations } from '../../lib/conversations';
import { useDuplexer } from '../../lib/use-duplexer';
import { useMe } from '../../lib/me';
import { useNarrow } from '../../lib/use-narrow';
import { useBasePath } from '../../lib/use-base-path';
import { topShowsMenu } from '../../lib/top-shows-menu';
import { biPageName } from '../../lib/bi-page-name';
import { recordNavigation } from '../../lib/drawer-nav';
import { useAppBack } from '../../lib/use-app-back';
import { useClientControlDuplexer } from '../../lib/use-client-control-duplexer';
import { savePhonePrefill } from '../../lib/phone-prefill';
import { clientControlTarget, isHomeTarget, isOverlayTarget } from '../../lib/client-control';
import { useActiveConversation, type ChatMode } from '../chat/ActiveConversationContext';
import { useChatOverlayOpen } from '../../lib/use-chat-overlay';
import { useOnboardingEntry } from '../../lib/use-onboarding-entry';
import { usePresenceReporter } from '../../lib/use-presence-reporter';
import { HomepageProvider, useHomepage } from '../HomepageContext';
import { useMyAgents } from '../../lib/my-agents';
import { prefetchDashboardMeta } from '../../lib/dashboard';
import { prefetchPacks } from '../../lib/packs';
import { DashboardHost, DashboardHostContext } from './DashboardHost';
import { FlyToPillProvider, useRegisterPillEl } from './FlyToPill';
import { SoundWave } from './AgentsIslandBadge';
import { VoiceCallPresenceProvider } from '../../lib/voice-call-presence-context';
import { VoiceCallIsland, VoiceCallStrip } from './VoiceCallIsland';
import { AppBanner } from './AppBanner';
import { ScrollArea } from './ui';
import { useShowAppBanner } from '../../lib/use-app-banner';
import { useNativeAppBannerImpression } from '../../lib/use-native-app-banner-bi';
import { APP_BANNER_HEIGHT_PX } from '../../lib/app-banner';

// Dev-only fake agents pill — looks like the real AgentsIslandBadge and registers
// itself as the fly target when no real pill is mounted (no Wix auth on localhost).
const SEED_AVATARS = ['var(--ds-avatar-03)', 'var(--ds-avatar-06)', 'var(--ds-avatar-05)', 'var(--ds-avatar-04)', 'var(--ds-accent-07)'];
function DevSeedPill() {
  const register = useRegisterPillEl();
  return (
    <div
      ref={register}
      aria-hidden
      style={{
        position: 'fixed',
        top: 18,
        insetInlineEnd: 28,
        zIndex: 9998,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        borderRadius: 999,
        background: 'var(--sym-accent-bg)',
        border: '1px solid var(--sym-accent)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {SEED_AVATARS.map((color, i) => (
          <span
            key={i}
            style={{
              marginInlineStart: i === 0 ? 0 : -8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: color,
              border: '1.5px solid var(--sym-accent-bg)',
              display: 'inline-block',
            }}
          />
        ))}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontFamily: 'var(--brand-font-display)', fontWeight: 'var(--sym-weight-semibold)', fontSize: 20, lineHeight: '24px', letterSpacing: '-0.4px', color: 'var(--sym-ink)' }}>5</span>
          <span style={{ fontWeight: 'var(--sym-weight-semibold)', fontSize: 12, letterSpacing: '-0.36px', color: 'var(--sym-ink)' }}>Agents</span>
        </span>
        <SoundWave active />
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
          <span style={{ fontFamily: 'var(--brand-font-display)', fontWeight: 'var(--sym-weight-semibold)', fontSize: 20, lineHeight: '24px', letterSpacing: '-0.4px', color: 'var(--sym-ink)' }}>12</span>
          <span style={{ fontWeight: 'var(--sym-weight-semibold)', fontSize: 12, letterSpacing: '-0.36px', color: 'var(--sym-ink)' }}>Skills</span>
        </span>
      </span>
    </div>
  );
}

// The top-right agents island. Lives inside HomepageProvider so it can drop off the empty
// home ("One home for everything", Figma 10941:75527) and the empty Your-Agents screen
// (10941:75358) — neither frame carries the agents badge. On any other route the pathname
// guards leave it alone.
function TopBarIsland({ narrow }: { narrow: boolean }) {
  const router = useRouter();
  const { state } = useHomepage();
  const agents = useMyAgents();
  const homeEmpty = router.pathname === '/' && state.status === 'absent';
  const agentsEmpty =
    router.pathname === '/agents' && agents.status === 'ready' && agents.team.agents.length === 0;
  if (homeEmpty || agentsEmpty) return null;
  return (
    <div
      style={{
        position: 'absolute',
        // Desktop: pinned to the top of the page header (72px pane top padding) and to the
        // content's side gutter — the same `80px + centering` inline padding the content
        // pane uses — so the pill's right edge lines up with the page content, not the raw
        // window edge. --sidepanel-w is threaded through so the pill rides left with the
        // content when the side panel pushes it. NOTE the chrome vars are subtracted HERE
        // but no longer in the pane's own padding: this box hangs off <main>, so its 100%
        // is the whole window, while the pane's is `.orion-content-region` — which has
        // already had the rail and the chat taken out of it. Absolute to
        // <main> (outside the scroll pane) so it stays put as the page scrolls. Narrow keeps
        // the mobile top-bar placement, top-aligned with the 40px menu button (both top:24).
        top: narrow ? 24 : 72,
        insetInlineEnd: narrow
          ? 12
          : 'calc(var(--chat-w, 0px) + var(--sidepanel-w, 0px) + 80px + max(0px, (100% - var(--sidebar-w, 280px) - var(--chat-w, 0px) - var(--sidepanel-w, 0px) - 1280px) / 2))',
        zIndex: 6,
      }}
    >
      <GenericEventsRouteProvider value="agents-island">
        <AgentsIslandBadge />
      </GenericEventsRouteProvider>
    </div>
  );
}

// Persistent frame mounted by _app.tsx. Owns the layout chrome (sidebar,
// brand header, drawer state, backdrop) so a route change swaps only the
// content area — the sidebar's DOM element survives, which is what lets the
// narrow-mode drawer slide animation play *through* a page change instead
// of getting cut off when the old page's sidebar unmounts.
export function AppFrame({ children }: { children: ReactNode }) {
  const router = useRouter();
  const me = useMe();
  const { state: conversations, refresh: refreshConversations } = useConversations(true);
  const narrow = useNarrow();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Search dialog (the header search icon opens it; the narrow drawer's header
  // icon too). Lifted here so it sits above both the rail and BrandHeader.
  const [searchOpen, setSearchOpen] = useState(false);
  // Wide-mode rail collapse (icon-only strip). Persisted so it survives reloads.
  // Ignored on narrow, where the sidebar is an off-canvas drawer instead.
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('symphony-sidebar-collapsed') === '1') setCollapsed(true);
    } catch {
      /* localStorage unavailable — default expanded */
    }
  }, []);
  const persistCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem('symphony-sidebar-collapsed', next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem('symphony-sidebar-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Trackpad gesture: a horizontal two-finger swipe ANYWHERE in the app
  // collapses (fingers left) or expands (fingers right) the rail. The browser's
  // own back/forward swipe is already suppressed app-wide (overscroll-behavior),
  // so the whole page is safe to claim — EXCEPT a genuinely x-scrollable element
  // under the cursor (a wide table, a code block), which keeps its scroll.
  // Vertical scrolling is untouched (only a dominantly-horizontal wheel counts),
  // and a threshold + cooldown make one gesture (plus its momentum tail) flip
  // the rail exactly once. Wheels inside the dashboard iframe can't reach this
  // document, so the gesture works everywhere except over that embedded pane.
  useEffect(() => {
    if (narrow) return;
    let acc = 0;
    let cooldownUntil = 0;
    const insideXScroller = (el: HTMLElement | null): boolean => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        if (n.scrollWidth > n.clientWidth + 1) {
          const overflowX = getComputedStyle(n).overflowX;
          if (overflowX === 'auto' || overflowX === 'scroll') return true;
        }
      }
      return false;
    };
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (insideXScroller(e.target as HTMLElement | null)) return;
      e.preventDefault();
      const now = Date.now();
      if (now < cooldownUntil) {
        acc = 0;
        return;
      }
      acc += e.deltaX;
      if (acc >= 60) {
        persistCollapsed(true);
        acc = 0;
        cooldownUntil = now + 600;
      } else if (acc <= -60) {
        persistCollapsed(false);
        acc = 0;
        cooldownUntil = now + 600;
      }
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [narrow, persistCollapsed]);

  // Warm the home dashboard existence probe on mount so the empty-state decision is ready by the time the user
  // lands on `/` (or returns to it) — the document itself streams via the iframe `src`. Also warm the packs
  // list, whose /api/packs read is slow, so opening Packs doesn't sit on a spinner.
  useEffect(() => {
    prefetchDashboardMeta();
    prefetchPacks();
  }, []);

  // Warm the rail destinations' route bundles so the first click doesn't pay the
  // chunk fetch mid-transition (no-op in dev, where Next disables prefetch).
  useEffect(() => {
    void router.prefetch('/agents').catch(() => {});
    void router.prefetch('/').catch(() => {});
    void router.prefetch('/packs').catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime cross-device / cross-tab sidebar refresh. Mobile does the
  // equivalent via react-query refetch on duplexer push. The chat page also
  // subscribes to the same channel for in-conversation message updates; this
  // second subscription is for the sidebar's "a new conversation appeared
  // somewhere" case. Debounced so a burst of stream events doesn't fan out
  // into a burst of GET /api/conversations.
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useDuplexer(() => {
    if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    refreshDebounceRef.current = setTimeout(() => {
      void refreshConversations();
    }, 600);
  });
  useEffect(() => {
    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    };
  }, []);

  // Orion drives the web client from the server (the ClientControl navigate
  // command): route to the target screen and open this conversation in the
  // floating chat via ?conversation=<id>. HTTPS-only (duplexer); manual
  // navigation covers the rest.
  const basePath = useBasePath();
  const { activeConversationId, setActiveConversationId, chatMode, setChatMode } =
    useActiveConversation();
  useClientControlDuplexer((command) => {
    const desktop = command.link?.desktop;
    if (command.action !== 'navigate' || !desktop || !command.conversationId) return;
    // Stashed rather than carried in the route: the step mounts from the URL, and a number in a URL is
    // reported to every analytics tag on the page.
    if (command.phone) savePhonePrefill(command.phone);
    setActiveConversationId(command.conversationId);
    // Symphony "navigate and shrink": the reply rides over the screen it sent the
    // user to as the side-docked chat. Home is the chat's own surface, so collapse
    // to the bubble there and let the home screen breathe; on narrow the chat is
    // full-screen and would hide the screen just navigated to, so collapse there
    // too. The size rides in the pushed URL (?chat) as well as in memory, so a
    // remount / refresh / the home route's SSR round-trip restores it.
    // An overlay step keeps the size it found: it opens on top of this conversation rather than
    // navigating away from it, so there is nothing to make room for.
    const mode: ChatMode = isOverlayTarget(desktop) ? chatMode : isHomeTarget(desktop) || narrow ? 'bubble' : 'expanded';
    setChatMode(mode);
    const target = clientControlTarget(desktop, command.conversationId, basePath, mode);
    void router.push(target.href, target.as);
  });

  // Onboarding entry — the always-mounted opener. The greeting itself lives in ChatSurface, which only
  // MOUNTS once the chat overlay is open (FloatingChat gates it), so a brand-new user needs something
  // outside that surface to open the chat. Derived from recipient state (`useOnboardingEntry` → the native
  // `onboardingRoute` port), NOT a `?welcome=true` param: `kickoff` opens fullscreen so ChatSurface mounts
  // and speaks first; `resume` reopens the in-progress onboarding thread fullscreen. Home only, once per
  // mount; ChatSurface's own ref guards the actual `startOnboarding`.
  //
  // Until onboarding is FINISHED the conversation IS the screen (native enterOnboarding/enterOnboardingResume
  // both open fullscreen), so `resume` forces fullscreen even when a thread is already active from the URL on
  // a refresh — a persisted `?chat=bubble` must not leave a mid-onboarding user stranded in the side dock.
  // Only `kickoff` needs the "no active thread" guard (a brand-new user has none, and it must not hijack a
  // thread the user is already reading).
  const { route: onboardingRoute, resumeConversationId } = useOnboardingEntry();
  const onboardingOpenedRef = useRef(false);
  useEffect(() => {
    if (onboardingOpenedRef.current) return;
    if (router.pathname !== '/') return;
    if (onboardingRoute === 'kickoff' && !activeConversationId) {
      onboardingOpenedRef.current = true;
      setChatMode('fullscreen');
    } else if (onboardingRoute === 'resume' && resumeConversationId) {
      onboardingOpenedRef.current = true;
      if (!activeConversationId) setActiveConversationId(resumeConversationId);
      setChatMode('fullscreen');
    }
  }, [router.pathname, activeConversationId, onboardingRoute, resumeConversationId, setChatMode, setActiveConversationId]);

  useEffect(() => {
    if (narrow) setSidebarOpen(false);
  }, [narrow]);

  useEffect(() => {
    const close = () => setSidebarOpen(false);
    router.events.on('routeChangeStart', close);
    return () => {
      router.events.off('routeChangeStart', close);
    };
  }, [router]);

  const chatOverlayOpen = useChatOverlayOpen();
  // The side-docked chat PUSHES the content pane via --chat-w; fullscreen covers
  // everything, so it doesn't reflow the pane.
  //
  // `chatDocked` MUST mirror exactly the condition under which FloatingChat paints its docked `dockWidth`
  // column (`open && !narrow && !fullscreen`). It used to require `chatMode ∈ {expanded, panel}`, which
  // disagreed with FloatingChat when the overlay is FORCE-opened (onboarding: `useChatOverlayOpen` returns
  // true on the home route regardless of chatMode). A force-opened chat with `chatMode==='bubble'` painted a
  // docked column while --chat-w stayed 0, so the pane never reflowed and the chat sat ON TOP of the
  // full-width home — the pack-accept user's overlap bug. Keying both off `!fullscreen` keeps them in lockstep.
  const chatFullscreen = chatMode === 'fullscreen';
  const chatDocked = !narrow && chatOverlayOpen && !chatFullscreen;
  const chatDockWidth = useChatDockWidth();

  // The mobile-web "get the app" banner. `useShowAppBanner` owns the gate (narrow +
  // not-dismissed + not-iOS-Safari) and hands down the install state for its CTA; AppFrame adds one frame-level
  // condition: never while a narrow chat overlay is up, since that overlay covers the
  // whole viewport (the page content is already `visibility:hidden` then). When shown
  // the flow banner sits above <main> and shrinks it by --app-banner-h, so all of the
  // top chrome (hamburger, island) and the content pane — positioned inside <main> —
  // reflow down together with no per-surface offset.
  const appBanner = useShowAppBanner();
  const showBanner = appBanner.show && !(chatOverlayOpen && narrow);
  // iOS Safari shows Apple's uninstrumentable native banner instead of our strip; log a
  // best-effort "offered" impression there so that surface isn't an analytics blind spot.
  useNativeAppBannerImpression();
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-banner-h', showBanner ? `${APP_BANNER_HEIGHT_PX}px` : '0px');
    return () => {
      root.style.removeProperty('--app-banner-h');
    };
  }, [showBanner]);
  // The rail's collapsed state is the user's alone: only the collapse button, the
  // click-to-expand affordance and the trackpad swipe change it. Opening the chat —
  // side-docked or fullscreen — deliberately leaves the rail exactly as the user set
  // it (this used to auto-collapse on dock and auto-expand on fullscreen).

  // The detail drill-ins are centered modals now (not a docked side panel), so opening one no longer
  // collapses the rail or reserves inline-end width — the rail is left exactly as the user set it.
  const railCollapsed = !narrow && collapsed;
  const railWidth = railCollapsed ? '57px' : '260px';

  // ===== The chip slot =====
  // One capsule at the top of the app, two things it can be: the green
  // "N Agents · N Skills" pill and the minimized call. They morph into each
  // other rather than swapping (lib/chip-morph.ts). These two booleans are the
  // pill's half of the answer — WHERE a pill exists — split out of the JSX so
  // the slot can be told about it, and so a call ending on a headerless screen
  // knows to leave the slot empty instead of conjuring a pill.
  const pillOnTopBar =
    (chatDocked
      ? true
      : router.pathname === '/agents' ||
        router.pathname === '/agents/[id]' ||
        !router.pathname.startsWith('/agents')) &&
    !(chatOverlayOpen && narrow) &&
    !(narrow && sidebarOpen) &&
    // On the home the island rides its own left-aligned row inside the home header (Mobile/
    // DesktopHomeHeader, Figma "Catch Up states" 7419:47452), so the top-bar copy is dropped there on
    // BOTH widths — it used to be dropped only on narrow.
    router.pathname !== '/' &&
    // Fullscreen chat draws its OWN island, centred in its header (ChatHeader) — mirroring native, and
    // necessary rather than cosmetic: the chat surface is a fixed z-50 layer, so this absolute z-6 pin is
    // covered by it and invisible there. So the top-bar copy steps aside, but `pillEligible` below still
    // counts it, because a pill DOES exist on that screen and the chip slot has to know (a minimized call
    // must have something to morph back into).
    !(chatOverlayOpen && chatFullscreen) &&
    // The plan picker owns its title row ("Pick a plan. Your AI team is
    // waiting."); the floating island collides with that header, so it's
    // dropped there (design QA — Monday 12732492560).
    router.pathname !== '/packages' &&
    // On narrow, the island is dropped on Your Agents (which draws its own team band —
    // headline + genome + roster — plus the tasks summary inline, making the floating
    // "N Agents · N Skills" pill redundant), on a single agent's detail, and on the Tasks
    // list. Mobile-web only; the desktop /agents, /agents/[id] and /tasks keep their island.
    !(narrow && (router.pathname === '/agents' || router.pathname === '/agents/[id]' || router.pathname === '/tasks'));
  // The home renders the island in its own header (both widths now), so the pill is still eligible
  // there — the chip slot must know a pill exists so a minimized call can morph into it.
  const pillOnHome = router.pathname === '/' && !(chatOverlayOpen && narrow) && !(narrow && sidebarOpen);
  // Fullscreen chat's island lives in its own header, so a pill exists there even though the top-bar copy is
  // dropped — the slot must count it or a call minimized on that screen morphs into nothing.
  // On a phone every open chat covers the screen, so its header carries the island there too — `chatFullscreen`
  // is a wide-only mode (FloatingChat gates it on `!narrow`).
  const pillInFullscreenChat = chatOverlayOpen && (narrow || chatFullscreen);
  const pillEligible = useAgentsPillReady() && (pillOnTopBar || pillOnHome || pillInFullscreenChat);

  // <main> below carries --sidebar-w for the panes, which is server-rendered, so the
  // rail is already the right width at first paint. A dialog portals to <body> —
  // OUTSIDE <main> — where that declaration doesn't resolve, which is why the
  // pane-scoped scrims used to measure the rail once on open and then go stale.
  // Mirroring it on :root lets them track the live width in pure CSS instead.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-w', railWidth);
    return () => {
      root.style.removeProperty('--sidebar-w');
    };
  }, [railWidth]);

  // Report chat presence app-wide so the push gate can suppress the phone buzz
  // while the user is actively viewing a conversation here (brand-neutral).
  usePresenceReporter();

  const toggleDrawer = useCallback(() => setSidebarOpen((o) => !o), []);

  // Dev-only: seed a MilestoneCard to preview its entrance reel (badge draw-in + confetti).
  const [seedCard, setSeedCard] = useState(false);
  const seedKinds = ['plan', 'workspace', 'dashboard', 'scan'] as const;
  const [seedKindIdx, setSeedKindIdx] = useState(0);
  const showSeed = useCallback(() => {
    setSeedCard(true);
    setSeedKindIdx((i) => (i + 1) % seedKinds.length);
  }, [seedKinds.length]);
  useEffect(() => {
    if (!seedCard) return;
    const t = window.setTimeout(() => setSeedCard(false), 3200);
    return () => window.clearTimeout(t);
  }, [seedCard]);

  // Optimistic nav state: highlight the destination the moment the user clicks,
  // not when the route lands. A transition can take seconds (chunk + data behind
  // a busy connection pool), and with no immediate response the click feels dead
  // — users click again. routeChangeStart fires synchronously on push.
  const [pendingNav, setPendingNav] = useState<NavId | undefined>(undefined);
  useEffect(() => {
    const navIdFor = (url: string): NavId | undefined => {
      const p = url.replace(/^\/(?:symphony|orion)-web/, '').split('?')[0] || '/';
      if (p === '/') return 'feed';
      if (p.startsWith('/agents') || p === '/knowledge') return 'agents';
      return undefined;
    };
    const start = (url: string) => setPendingNav(navIdFor(url));
    const done = () => setPendingNav(undefined);
    router.events.on('routeChangeStart', start);
    router.events.on('routeChangeComplete', done);
    router.events.on('routeChangeError', done);
    return () => {
      router.events.off('routeChangeStart', start);
      router.events.off('routeChangeComplete', done);
      router.events.off('routeChangeError', done);
    };
  }, [router]);

  const navCurrent: NavId | undefined =
    pendingNav ??
    (() => {
      const p = router.pathname;
      // `/` is the home route — on Symphony it renders the Feed (homepage grid).
      if (p === '/') return 'feed';
      // The activity log is its own rail destination (both phones have always had it).
      // The agents page and its remaining sub-destinations (the knowledge card, and the
      // legacy `/agents/activity` deeplink) light up "Your agents".
      if (p.startsWith('/agents') || p === '/knowledge') return 'agents';
      return undefined;
    })();

  // The active conversation is the store-backed truth (seeded from the
  // `?conversation` query param, switched by the sidebar / picker). It no longer
  // lives in a route path segment.
  const selectedConversationId = activeConversationId;

  // The sidebar highlights exactly ONE surface — the one actually on screen. `activeConversationId`
  // persists as the resumable pointer even while the chat is a closed bubble, so highlighting a chat
  // row off it would light up a conversation UNDER the page you're looking at (two rows selected at
  // once). So a chat row lights up only while its overlay is visible; when it's closed, only the page
  // destination is active. And on narrow an open chat COVERS the page, so the page drops its highlight
  // while the chat is up (on wide the chat docks beside the page, so both stay legitimately visible).
  const sidebarSelectedId = chatOverlayOpen ? selectedConversationId : null;
  const sidebarNavCurrent = narrow && chatOverlayOpen ? undefined : navCurrent;

  // The leading header's BACK-vs-MENU decision — the web side of native's `topShowsMenu`
  // (see lib/top-shows-menu.ts). It reads the per-navigation `openedFromDrawer` flag carried
  // per history entry (lib/drawer-nav.ts): a screen reached from the drawer/rail shows the
  // menu, everything else shows back. `fromDrawer` is React state so it refreshes after each
  // navigation records its entry.
  const [fromDrawer, setFromDrawer] = useState(false);
  useEffect(() => {
    const update = () => setFromDrawer(recordNavigation());
    update(); // the entry present on mount (a fresh load / deep-link is never "from drawer")
    router.events.on('routeChangeComplete', update);
    router.events.on('routeChangeError', update);
    window.addEventListener('popstate', update);
    return () => {
      router.events.off('routeChangeComplete', update);
      router.events.off('routeChangeError', update);
      window.removeEventListener('popstate', update);
    };
  }, [router]);

  // The header back button is the MOBILE back affordance only. On desktop the back button lives in
  // the page content (the Breadcrumb slot) instead of the sidebar. Suppressed while a narrow chat
  // overlay is open — the header is hidden then anyway, and a back would navigate the route out
  // from under the chat.
  const showBack =
    narrow && !topShowsMenu(router.pathname, fromDrawer) && !chatOverlayOpen;
  const onBack = useAppBack();

  return (
    <FlyToPillProvider>
    {/* Wraps the WHOLE frame because its two ends are far apart: the call is
        published from inside <FloatingChat> (a voice turn is a turn in that
        conversation, so it needs the chat's plumbing) and the island is consumed
        out here in the chrome, where it can float above every screen including a
        collapsed chat. See lib/voice-call-presence-context.tsx. */}
    <VoiceCallPresenceProvider>
    {/* One capsule, two identities. The pill is laid out by the header and the
        island is fixed and outlives navigation, so neither can transition into
        the other in place; the slot provider owns the swap and its overlay
        layer draws the capsule while it changes shape. */}
    <ChipSlotProvider pillEligible={pillEligible}>
    {showBanner && (
      <GenericEventsRouteProvider value="app-banner">
        <AppBanner installed={appBanner.installed} onDismiss={appBanner.dismiss} />
      </GenericEventsRouteProvider>
    )}
    {/* Mobile-web ONLY: the minimized call rides in an in-flow strip above <main>
        (below the app-download banner, above every screen header), reserving its own
        height so it pushes the header + content down instead of floating over them.
        It sets --voice-island-h, which <main> subtracts below. Off-narrow this renders
        nothing and the fixed island (bottom of the tree) is used instead. */}
    <GenericEventsRouteProvider value="voice-call">
      <VoiceCallStrip narrow={narrow} />
    </GenericEventsRouteProvider>
    <main
      style={{
        ...({
          '--sidebar-w': railWidth,
          '--chat-w': chatDocked ? `${chatDockWidth}px` : '0px',
          // The task/goal side panel is a floating card on the inline-end edge that
          // PUSHES the content pane (via .orion-content-pane's --sidepanel-w reflow)
          // No docked side panel any more (details are centered modals), so nothing is reserved
          // here; the var stays defined at 0 because the content-pane width calc still reads it.
          '--sidepanel-w': '0px',
        } as React.CSSProperties),
        // The app-download banner AND the mobile-web voice strip (when shown) are flow
        // siblings above <main>; shrink <main> by their heights so all three sum to the
        // viewport instead of overflowing it.
        minHeight: 'calc(100dvh - var(--app-banner-h, 0px) - var(--voice-island-h, 0px))',
        backgroundColor: 'var(--field-color)',
        color: 'rgba(var(--ink-rgb), 0.95)',
        fontFamily:
          'var(--brand-font-ui, ui-sans-serif, -apple-system, "SF Pro Display", system-ui, sans-serif)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CosmicBackdrop />

      {narrow && sidebarOpen && (
        <div
          aria-hidden
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'absolute',
            inset: 0,
            // Above the full-screen chat (z50) when one is open, so the drawer
            // it backs can dim the chat too.
            zIndex: chatOverlayOpen ? 55 : 5,
            // The mobile menu's modal overlay (Figma 14039-170869): the "Transpert
            // overlay" wash (#0f0e0e — closest token is Neutral/950) at 30% over the
            // "Modal overlay" 20px background blur, so the drawer reads as a modal
            // layer floating above a pushed-back, blurred room. Prefixed for iOS
            // Safari (mobile-web only).
            backgroundColor: 'rgba(var(--ds-neutral-950-rgb), 0.3)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        />
      )}

      {/* Chrome-area route roots: every generic event fired from inside these
          trees inherits this segment as the start of its componentRoute. */}
      <GenericEventsRouteProvider value="sidebar">
        <Sidebar
          state={conversations}
          selectedId={sidebarSelectedId}
          navCurrent={sidebarNavCurrent}
          me={me}
          narrow={narrow}
          isOpen={sidebarOpen}
          onRequestClose={narrow ? () => setSidebarOpen(false) : undefined}
          elevated={narrow && chatOverlayOpen}
          collapsed={railCollapsed}
          onExpand={() => persistCollapsed(false)}
          onOpenSearch={() => setSearchOpen(true)}
        />
      </GenericEventsRouteProvider>
      <GenericEventsRouteProvider value="brand-header">
        <BrandHeader
          narrow={narrow}
          sidebarOpen={sidebarOpen}
          onToggle={toggleDrawer}
          onToggleCollapsed={toggleCollapsed}
          elevated={narrow && chatOverlayOpen}
          collapsed={railCollapsed}
          largeMark={router.pathname === '/'}
          onOpenSearch={() => setSearchOpen(true)}
          showBack={showBack}
          onBack={onBack}
          backPageName={biPageName(router.pathname, router.query)}
        />
      </GenericEventsRouteProvider>
      {searchOpen && (
        <SearchDialog
          state={conversations}
          selectedId={selectedConversationId}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* Team-summary pill — the top-right counterpart to the brand header. Shows on every
          internal page except the Your Agents / Account *pages* (which list the counts
          inline) and the EMPTY home / empty Your-Agents (those frames carry no badge). The
          route half is `pillOnTopBar`; the empty-state half needs the homepage/agents reads,
          so the pill lives in `TopBarIsland` inside HomepageProvider. */}
      <HomepageProvider>
        {pillOnTopBar && <TopBarIsland narrow={narrow} />}
        {/* The content REGION — the box between the rail and whatever is docked on the inline-end
            edge. It owns those two insets (and their 420ms reflow); every `.orion-content-pane`
            inside simply fills it. It exists to be the horizontal SCROLLER: a page holds a 900px
            floor, so on a window (or a chat drag) that leaves less than that, the page overflows
            this box and the user can scroll to the part the chat is sitting over. It has to be a
            positioned ancestor of the panes to scroll them at all — an overflow container does not
            clip an absolutely-positioned descendant whose containing block is further out — which
            is why the insets moved here from the panes rather than a scroller being wrapped around
            them. The sideways scroll is a `ScrollArea` like every other scroll container in the app
            (it brings the designed bar); its geometry is globals.css `.orion-content-region`. */}
        <ScrollArea axis="horizontal" className="orion-content-region">
          <DashboardHost />
          <DashboardHostContext.Provider value={true}>
            <div
              key={router.pathname}
              className="orion-route-fade"
              style={chatOverlayOpen && narrow ? { visibility: 'hidden' } : undefined}
            >
              {children}
            </div>
          </DashboardHostContext.Provider>
        </ScrollArea>
        <GenericEventsRouteProvider value="chat">
          <FloatingChat />
        </GenericEventsRouteProvider>
      </HomepageProvider>
      <TaskSidePanel />
      {/* Phone verification as a step over whatever is showing, opened by `?verify=phone|connect|code`.
          It sits at the frame rather than on the home because every surface that offers a WhatsApp
          action has to be able to ask, and a caller mid-task on one of them must land back on it. Routes
          with their own phone screens (`/onboarding`) are outside AppFrame. Self-gates on the param. */}
      <PhoneVerifyModal />
      {process.env.NODE_ENV === 'development' && (
        <>
          <button
            onClick={showSeed}
            aria-label="Seed fly-to-pill animation"
            style={{
              position: 'fixed',
              bottom: 16,
              insetInlineStart: 16,
              zIndex: 9999,
              background: 'var(--sym-accent)',
              border: 'none',
              borderRadius: 999,
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--sym-ink)',
              cursor: 'pointer',
              opacity: 0.85,
            }}
          >
            ⚡ seed
          </button>
          {seedCard && (
            <div
              style={{
                position: 'fixed',
                bottom: 56,
                insetInlineStart: 16,
                zIndex: 9999,
              }}
            >
              <MilestoneCard
                key={String(seedCard) + seedKindIdx}
                card={{
                  kind: seedKinds[seedKindIdx],
                  name: seedKinds[seedKindIdx] === 'plan' ? 'Weekly check-in' : seedKinds[seedKindIdx] === 'workspace' ? 'Growth' : 'Home',
                }}
              />
            </div>
          )}
          {/* Fake pill shown in dev when no real AgentsIslandBadge is mounted (no auth context). */}
          {seedCard && (
            <DevSeedPill />
          )}
        </>
      )}
    </main>
    {/* Outside <main> on purpose: the FLOATING island is `position: fixed` and must not
        be clipped or offset by the frame's transforms/filters, and it has to outlive
        every route change — which is exactly what minimizing a call means. On narrow it
        renders nothing (the in-flow strip above <main> owns that width instead). */}
    <GenericEventsRouteProvider value="voice-call">
      <VoiceCallIsland narrow={narrow} />
    </GenericEventsRouteProvider>
    </ChipSlotProvider>
    </VoiceCallPresenceProvider>
    </FlyToPillProvider>
  );
}
